import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import pricingApi from '../../../APIs/pricing';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import {
  Button,
  Divider,
  NavigateBack,
  Paragraph,
  Typography,
} from '../../../designComponents';
import { RootState } from '../../../store/selectors';
import {
  InfoRowStyled,
  VerticalSpaceStyled,
} from '../../../style/reusableStyle';
import { humanReadableDate, requestAPI } from '../../../utils';
import { PricingDetailsContainerStyled } from './style';

type Props = {};

const ViewPricing = (_props: Props) => {
  const { pricingId } = useParams();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isOpenStatus, setIsOpenStatus] = useState(false);
  const [statusToChange, setStatusToChange] = useState<boolean | null>(null);
  const pricingDetails = useSelector(
    (state: RootState) => state.pricingDetails
  );

  const getPricingDetails = async (id: string) => {
    try {
      const response = await requestAPI(pricingApi.getPricingDetail(id));
      console.log('Fetched Pricing Details:', response);
      setIsActive(response.data.isActive);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (pricingId) {
      getPricingDetails(pricingId);
    }
  }, [pricingId]);

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await requestAPI(pricingApi.deletePricing(id));
      setLoading(false);
      setIsOpenDelete(false);
      toast.success('Pricing deleted successfully');
      navigate(-1);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (status: boolean) => {
    if (!pricingId) return;
    try {
      setLoading(true);
      const response = await requestAPI(
        pricingApi.statusUpdatePricingPlan(pricingId, status)
      );
      console.log('Status Update Response:', response);
      setLoading(false);
      setIsActive(status);
      toast.success('Pricing status updated successfully');

      getPricingDetails(pricingId);
    } catch (error) {
      console.error(error);
      setLoading(false);
      toast.error('Failed to update pricing status');
    }
  };

  const confirmStatusChange = (status: boolean) => {
    setStatusToChange(status);
    setIsOpenStatus(true);
  };

  return (
    <PricingDetailsContainerStyled>
      <NavigateBack />
      <Typography
        variant="normal"
        strong
        fontColor="secondary"
        className="section-title"
      >
        Pricing Information
      </Typography>
      <VerticalSpaceStyled>
        <InfoRowStyled>
          <Typography className="title" fontColor="dark">
            Created At:
          </Typography>
          <Typography className="value">
            {humanReadableDate(pricingDetails?.createdAt)}
          </Typography>
        </InfoRowStyled>
        <InfoRowStyled>
          <Typography className="title" fontColor="dark">
            Title:
          </Typography>
          <Typography className="value">{pricingDetails?.title}</Typography>
        </InfoRowStyled>
        <InfoRowStyled>
          <Typography className="title" fontColor="dark">
            Plan Type:
          </Typography>
          <Typography className="value">{pricingDetails?.planType}</Typography>
        </InfoRowStyled>
        <InfoRowStyled>
          <Typography className="title" fontColor="dark">
            Price:
          </Typography>
          <Typography className="value">{pricingDetails?.price}</Typography>
        </InfoRowStyled>
        <InfoRowStyled>
          <Typography className="title" fontColor="dark">
            Price Denom:
          </Typography>
          <Typography className="value">
            {pricingDetails?.priceDenom}
          </Typography>
        </InfoRowStyled>
        <InfoRowStyled>
          <Typography className="title" fontColor="dark">
            Recurring Period:
          </Typography>
          <Typography className="value">
            {pricingDetails?.recurringPeriod}
          </Typography>
        </InfoRowStyled>
        <InfoRowStyled style={{ gap: '50px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Typography className="title" fontColor="dark">
              Active:
            </Typography>
            <Typography className="value">
              {pricingDetails.isActive === true ? 'Yes' : 'No'}
            </Typography>
          </div>
          <Button
            variant={isActive ? 'danger' : 'primary'}
            isLoading={loading && isActive !== null}
            onClick={() => confirmStatusChange(!isActive)}
            disabled={loading}
          >
            {isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </InfoRowStyled>
        <InfoRowStyled>
          <Typography className="title" fontColor="dark">
            Recurring:
          </Typography>
          <Typography className="value">
            {pricingDetails?.isRecurring ? 'Yes' : 'No'}
          </Typography>
        </InfoRowStyled>
        <InfoRowStyled $direction="column">
          <Typography className="title" fontColor="dark">
            Notes:
          </Typography>
          <div className="value">
            <Paragraph text={pricingDetails?.notes} />
          </div>
        </InfoRowStyled>
      </VerticalSpaceStyled>
      <div style={{ marginTop: '30px' }}>
        <Button
          variant="danger"
          isLoading={loading}
          onClick={() => setIsOpenDelete(true)}
        >
          Delete
        </Button>
      </div>
      <Divider />

      <ConfirmationModal
        message="Are you sure you want to delete this pricing?"
        isOpen={isOpenDelete}
        cancelText="No"
        okText="Yes"
        onClose={() => setIsOpenDelete(false)}
        onOk={() => pricingId && handleDelete(pricingId)}
      />

      <ConfirmationModal
        message={`Are you sure you want to ${
          statusToChange ? 'activate' : 'deactivate'
        } this pricing?`}
        isOpen={isOpenStatus}
        cancelText="No"
        okText="Yes"
        onClose={() => setIsOpenStatus(false)}
        onOk={() => {
          if (statusToChange !== null) {
            handleStatusChange(statusToChange);
            setIsOpenStatus(false);
          }
        }}
      />
    </PricingDetailsContainerStyled>
  );
};

export default ViewPricing;
